import axios from "axios";

const baseUrl = "/api/recurring-schedules";

const throwResponseData = (error) => {
  throw error.response?.data ?? error;
};

export const getRecurringSchedules = async (companyKey, filters = {}) => {
  try {
    const response = await axios.get(baseUrl, {
      params: { companyKey, ...filters },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * The active schedules and how close each is to raising its next order: a next run date on a
 * calendar schedule, consumed against the interval on a usage one. On an asset node and
 * everything under it, or across the company when no assetNodeId is given. Overdue first.
 *
 * Fields that mean nothing on a schedule's basis come back null — a usage schedule has no next
 * run date, a calendar one no percentage — so a caller can branch on null rather than on basis.
 */
export const getUpcomingRecurringSchedules = async (companyKey, { assetNodeId } = {}) => {
  try {
    const response = await axios.get(`${baseUrl}/upcoming`, {
      params: { companyKey, ...(assetNodeId ? { assetNodeId } : {}) },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * The work orders the calendar schedules will raise from `from` up to `to`, before they exist.
 * The server works them out from each schedule as it stands now and saves nothing. Usage
 * schedules are left out, because they have no date to give. `from` and `to` are instants as ISO
 * strings, and the range is at most 26 weeks.
 *
 * Each item has the fields the order will be given, plus a dueDate, which is the UTC day the
 * nightly sweep raises it. A run that is already due but not raised yet comes back once, marked
 * isOverdue, and only when the range includes now.
 */
export const getProjectedRecurringSchedules = async (companyKey, from, to) => {
  try {
    const response = await axios.get(`${baseUrl}/projected`, {
      params: { companyKey, from, to },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const getRecurringScheduleById = async (companyKey, id) => {
  try {
    const response = await axios.get(`${baseUrl}/${id}`, { params: { companyKey } });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const createRecurringSchedule = async (schedule) => {
  try {
    const response = await axios.post(baseUrl, schedule);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const updateRecurringSchedule = async (id, schedule) => {
  try {
    const response = await axios.put(`${baseUrl}/${id}`, schedule);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const deleteRecurringSchedule = async (companyKey, id) => {
  try {
    const response = await axios.delete(`${baseUrl}/${id}`, { params: { companyKey } });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const activateRecurringSchedule = async (companyKey, id) => {
  try {
    const response = await axios.post(`${baseUrl}/${id}/activate`, null, { params: { companyKey } });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const deactivateRecurringSchedule = async (companyKey, id) => {
  try {
    const response = await axios.post(`${baseUrl}/${id}/deactivate`, null, { params: { companyKey } });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * Put a usage schedule's counter back to zero, as of now.
 *
 * No resetByUserId in the body, and none accepted by the endpoint: the controller stamps it from
 * the token. Sending one would suggest it is respected.
 *
 * No date either. A reset is always now — backdating one would need an upper-bounded consumption
 * query the asset lane does not have, and the two lanes would then differ in what they can say.
 *
 * Cmms_Manager. A reset moves a service anchor, so the caller must be gated too — a control that
 * always 403s is a control that lies.
 */
export const resetRecurringSchedule = async (companyKey, id, notes) => {
  try {
    const response = await axios.post(`${baseUrl}/${id}/reset`, { companyKey, notes: notes || null });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/** What has reset this schedule's counter, newest first. Cmms_Field — reading is not resetting. */
export const fetchScheduleResetLog = async (companyKey, id, take) => {
  try {
    const response = await axios.get(`${baseUrl}/${id}/reset-log`, {
      params: { companyKey, ...(take ? { take } : {}) },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};
